import { useState, useEffect } from 'react';
import { Menu, X, Anchor } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#products', label: 'Products' },
    { href: '#how-it-works', label: 'How it Works' },
    { href: '#integrations', label: 'Integrations' },
    { href: '#contact', label: 'Contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass border-b border-gray-200/50 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="/"
            data-testid="logo"
            className="flex items-center gap-2 group"
          >
            <div className="w-10 h-10 bg-coastal-navy rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-bold text-coastal-navy text-lg leading-tight">
                Coastal Route
              </span>
              <span className="text-xs text-gray-500 font-medium tracking-wide">
                COMMERCE
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav
            data-testid="desktop-nav"
            className="hidden md:flex items-center gap-8"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-gray-600 hover:text-coastal-navy font-medium transition-colors relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-coastal-orange group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              data-testid="header-cta"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="bg-coastal-orange hover:bg-coastal-orange/90 text-white rounded-full px-6 py-2.5 font-semibold transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md"
            >
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-coastal-navy"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav
            data-testid="mobile-nav"
            className="md:hidden py-4 border-t border-gray-200/50"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-gray-600 hover:text-coastal-navy font-medium py-2 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Button
                data-testid="mobile-cta"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="bg-coastal-orange hover:bg-coastal-orange/90 text-white rounded-full font-semibold mt-2"
              >
                Get a Quote
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
